/**
 * Structure extraction — one job per exam.
 *
 * NOT splittable per main task, and it is worth saying why, because every other task-tree step in this
 * directory is: the step's first act is a single `runStructureAgent` pass over the source images that
 * DISCOVERS the tasks, followed by one `runBoundingBoxAgent` pass over the whole document. There is no task
 * list to split by until this job has run, and splitting the bbx pass per task would re-send the document
 * once per task.
 *
 * No `desired`: structure extraction is requested (the `process_files` tool), not implied by state. An exam
 * holding an unprocessed blank exam is not asking to be extracted — the teacher is still assigning files.
 */

import { logger } from '@adornis/base/logging.js';
import { defineJob } from '../registry.js';
import { taskTreeLockKey } from '../job.js';
import * as conditions from '../job-conditions.js';
import { runStructureStep } from '../../pipeline/structure-step.js';
import { structureExtractionEvents$ } from '../../start-structure-extraction.js';

export interface StructurePayload {
  /** The conversation to open the confirmation request in. Absent for a re-run outside a chat. */
  conversationId?: string;
  [key: string]: unknown;
}

export const structureJob = defineJob<StructurePayload>({
  type: 'structure',

  // The run is supplied by `dispatchStructureExtraction`, and NO `onTransition` closes it: `runStructureStep`
  // writes COMPLETED or ERROR itself, distinguishing outcomes the queue cannot see (task descriptions failed
  // but the structure is usable). That is why `task` supplies no `StepClosure` at all. Installing one here is
  // how the queue-derived closure would be opted into, once `step-completion.ts` grows one for the step.
  //
  // Posting the confirmation is NOT done here any more — see `structure-announce.job.ts`. That is a second
  // phase of this same step, dispatched from this handler once the structure is computed, and gated on the
  // split being resolved so the two steps' chat cards cannot race each other for a blank-exam upload.

  // Rewrites `exam.tasks` wholesale, so it shares the key with every other task-tree writer.
  /**
   * One row. `conversationId` is where the confirmation request opens, and the handler is what emits that event —
   * the confirmation message it carries only exists once the step has run.
   */
  /**
   * THE ONE TYPE THAT SEEDS OTHERS. Everything downstream gets a progress record the moment the structure is queued,
   * so the UI can show the whole pipeline from the start rather than growing it a step at a time. Seeded, never reset —
   * `StepStart.seed` says why that distinction cost an answer run its completion.
   */
  onStepStart: { seed: ['answer', 'masterSolution', 'gradingScheme'] },

  batch: {
    units: (_examId, scope) => {
      const { conversationId } = scope as { conversationId?: string };
      return [conversationId ? { conversationId } : {}];
    },
  },

  lockKey: examId => taskTreeLockKey(examId),

  ready: exam => {
    // A generated exam's tree was authored by the exam-creation tool, not read off a scan — running the
    // structure agent on its own compiled pages would OCR the document it just wrote and overwrite the
    // good tree with a worse one. `exam-creation.job.ts` closes this step and auto-confirms the structure
    // itself on success, so there is nothing left for this job to do. Dropped, not waited: the source
    // (`blankExamIds`, the compiled page images) is already there, so waiting would never resolve.
    if (conditions.generatedExam(exam)) {
      return { verdict: 'drop', because: 'structure:generated-exam' };
    }

    // The step reads a blank exam, or the first submission if there is none. With neither there is nothing
    // to read — but both can still arrive, so this waits rather than dropping.
    if (!conditions.hasBlankExam(exam) && !conditions.hasSubmissions(exam)) {
      return { verdict: 'wait', because: 'structure:no-source' };
    }
    return 'run';
  },

  // One structure pass is minutes of OCR over a whole exam, and the step is self-contained per attempt
  // (it re-reads everything from the exam), so a retry is safe.
  maxRuntimeMs: 20 * 60_000,
  maxAttempts: 3,

  handler: async (examId, payload, context) => {
    // The client's subscription lives on `structureExtractionEvents$`, and the event it actually acts on
    // carries the confirmation message — which only exists here, as the step's return value. Emitting from
    // the handler rather than from the mutation is what let the mutation stop awaiting the work.
    const result = await runStructureStep(examId, payload.conversationId).catch(err => {
      structureExtractionEvents$.next({ examID: examId, event: 'error', error: String(err) });
      throw err;
    });

    // `ok: false` is the step's own reported failure, and it has already written the error into
    // `examState` for the UI. Throwing retries it; the step is idempotent, so that is the wanted behaviour
    // for a transient OCR failure and harmless for a permanent one (attempts run out).
    if (!result.ok) {
      structureExtractionEvents$.next({ examID: examId, event: 'error', error: 'structure extraction failed' });
      throw new Error('Structure extraction reported a failure');
    }

    // Enqueued from HERE, while this row is still claimed, rather than from an `onStepComplete` follow-up: a
    // follow-up fires only once the `task` step has already drained, which would close this wave's batch, open
    // a second one, and announce twice for one upload. Enqueuing here keeps the step open across both phases —
    // same rule the `grading` phases follow (see `grading.job.ts`). Skipped when there is nothing to confirm
    // (no conversation, or the structure came back with no confirmation message at all).
    if (payload.conversationId && result.confirmationMessage) {
      const { enqueueForType } = await import('../enqueue.js');
      await enqueueForType(
        'structureAnnounce',
        examId,
        {
          conversationId: payload.conversationId,
          content: result.confirmationMessage,
          hasLowConfidence: result.hasLowConfidence,
        },
        { batchKey: context.job.batchKey },
      ).catch(err => logger.error({ err, examId }, 'Could not enqueue the structure confirmation'));
    }

    structureExtractionEvents$.next({ examID: examId, event: 'completed', result: 'structure extracted' });

    return { note: 'structure extracted' };
  },
});
